import React, { useState, useRef, useEffect, useCallback } from 'react';
import './CarShowcase.css';

// 模拟车辆数据
const carData = [
  {
    id: 1,
    brand: 'LIXIANG',
    model: 'L9',
    type: '增程版',
    image: '/image/carmodle/lixiang.png',
    logo: '/image/logo/lixiang-logo.png',
    specs: {
      mileage: '1100',
      speed: '215',
      year: '2024'
    },
    description: '理想L9是一款大型豪华智能电动SUV，采用增程式混合动力系统，拥有超长续航和强大性能，为家庭出行提供极致舒适与科技体验。'
  },
  {
    id: 2,
    brand: 'JAGUAR',
    model: 'F-TYPE R',
    type: 'TYPE R',
    image: '/image/carmodle/lixiang.png',
    logo: '/image/logo/jaguar-logo.png',
    specs: {
      mileage: '450',
      speed: '280',
      year: '2024'
    },
    description: 'Every tap of the accelerator is a gut punch. It\'s a special engine, a little talisman against boredom and the indignities of daily life.'
  },
  {
    id: 3,
    brand: 'MERCEDES',
    model: 'AMG GT',
    type: 'GT 53',
    image: '/image/carmodle/lixiang.png',
    logo: '/image/logo/mercedes-logo.png',
    specs: {
      mileage: '320',
      speed: '315',
      year: '2023'
    },
    description: 'The Mercedes-AMG GT combines the fascination of an authentic sports car with segment-specific technology leadership and high practicality.'
  },
  {
    id: 4,
    brand: 'PORSCHE',
    model: '911 TURBO',
    type: 'TURBO S',
    image: '/image/carmodle/lixiang.png',
    logo: '/image/logo/porsche-logo.png',
    specs: {
      mileage: '380',
      speed: '330',
      year: '2024'
    },
    description: 'The 911 Turbo S represents the pinnacle of the 911 model line – a high-performance vehicle which takes the traditional 911 virtues to the extreme.'
  }
];

const CarShowcase = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [startX, setStartX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const mainRef = useRef(null);
  const currentCar = carData[currentIndex];

  const nextCar = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % carData.length);
  }, []);

  const prevCar = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + carData.length) % carData.length);
  }, []);
  
  const getNextIndex = useCallback(() => (currentIndex + 1) % carData.length, [currentIndex]);
  const getPrevIndex = useCallback(() => (currentIndex - 1 + carData.length) % carData.length, [currentIndex]);
  
  // 处理鼠标拖拽操作
  const handleMouseDown = useCallback((e) => {
    setStartX(e.clientX);
    setIsDragging(true);
  }, []);
  
  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    const currentX = e.clientX;
    const diff = currentX - startX;
    
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        prevCar();
      } else {
        nextCar();
      }
      setIsDragging(false);
    }
  }, [isDragging, startX, prevCar, nextCar]);
  
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);
  
  // 处理触摸操作
  const handleTouchStart = useCallback((e) => {
    setStartX(e.touches[0].clientX);
    setIsDragging(true);
  }, []);
  
  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    const currentX = e.touches[0].clientX;
    const diff = currentX - startX;
    
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        prevCar();
      } else {
        nextCar();
      }
      setIsDragging(false);
    }
  }, [isDragging, startX, prevCar, nextCar]);
  
  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);
  
  // 添加和移除事件监听器
  useEffect(() => {
    const mainElement = mainRef.current;
    if (mainElement) {
      mainElement.addEventListener('mousemove', handleMouseMove);
      mainElement.addEventListener('mouseup', handleMouseUp);
      mainElement.addEventListener('mouseleave', handleMouseUp);
      mainElement.addEventListener('touchmove', handleTouchMove);
      mainElement.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        mainElement.removeEventListener('mousemove', handleMouseMove);
        mainElement.removeEventListener('mouseup', handleMouseUp);
        mainElement.removeEventListener('mouseleave', handleMouseUp);
        mainElement.removeEventListener('touchmove', handleTouchMove);
        mainElement.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  return (
    <section className="car-showcase" id="car-showcase">
      <div className="car-showcase-header">
        <div className="left-header">
          <div className="showcase-label">
            <span>ARRIVE IN STYLE</span>
          </div>
          <div className="showcase-text">
            <p>Choose from our prestigious collection of world-class brands:</p>
          </div>
        </div>

        <div className="right-header">
          <div className="showcase-actions">
            <a href="#rent" className="showcase-btn rent-btn">Rent a car</a>
            <a href="#buy" className="showcase-btn buy-btn">Buy a car</a>
          </div>
        </div>
      </div>
      
      <div className="car-display-frame">
        <div 
          className="car-showcase-main" 
          ref={mainRef} 
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <div className="car-brand-container">
            <h1 className="car-brand">{currentCar.brand}</h1>
          </div>
          
          <div className="logo-container">
            <img 
              src={currentCar.logo}
              alt={`${currentCar.brand} Logo`}
              className="brand-logo"
            />
          </div>
          
          <div className="car-image-container">
            <img 
              src={currentCar.image}
              alt={`${currentCar.brand} ${currentCar.model}`}
              className="car-image"
            />
          </div>

          {/* 左侧缩略图预览 */}
          <div className="car-preview prev-car" onClick={prevCar}>
            <img 
              src={carData[getPrevIndex()].image}
              alt={`${carData[getPrevIndex()].brand} Preview`}
              className="preview-image"
            />
          </div>
          
          {/* 右侧缩略图预览 */}
          <div className="car-preview next-car" onClick={nextCar}>
            <img 
              src={carData[getNextIndex()].image}
              alt={`${carData[getNextIndex()].brand} Preview`}
              className="preview-image"
            />
          </div>
        </div>
      </div>
      
      <div className="car-info-container">
        <div className="car-model-info">
          <h3 className="car-model-name">{currentCar.brand} {currentCar.model}</h3>
          <p className="car-description">{currentCar.description}</p>
        </div>
        
        <div className="car-specs">
          <div className="spec-item">
            <span className="spec-value">{currentCar.specs.mileage}</span>
            <span className="spec-label">MILEAGE</span>
          </div>
          <div className="spec-item">
            <span className="spec-value">{currentCar.specs.speed}</span>
            <span className="spec-label">KM/H</span>
          </div>
          <div className="spec-item">
            <span className="spec-value">{currentCar.specs.year}</span>
            <span className="spec-label">YEAR</span>
          </div>
        </div>
        
        <div className="car-details">
          <button className="details-btn">View details</button>
        </div>
      </div>
    </section>
  );
};

export default CarShowcase; 